import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";

import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleCreateFolder } from "../../shared/services/folderUtils";

import FileManagementAppBar from "./component/FileManagementAppBar";
import FileManagementPanel from "./component/FileManagementPanel";
import DocumentClassifierPanel from "./component/DocumentClassifierPanel";

import FolderSelectionDialog from "./classification/classification_component/FolderSelectionDialog";
import { useFolder } from "../../context/FolderContext";

function UserDashboard() {
  const { currentUser } = useAuth();

  const navigate = useNavigate();

  const { folders, isLoading, error } = useFolder();

  const [folderDialogOpen, setFolderDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries(["userFolderMetadata"]);
  }, [queryClient]);

  const createFolderMutation = useMutation({
    mutationFn: async (folderName) => {
      if (!currentUser) throw new Error("User not authenticated");
      return await handleCreateFolder({ userId: currentUser.uid, folderName });
    },
  });

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleOpenFolderDialog = () => {
    setFolderDialogOpen(true);
  };

  const { selectedFolder, setSelectedFolder } = useFolder();

  const handleFolderSelect = (folder) => {
    console.log("UserDashboard: Selected folder before navigate:", folder);
    // Ensure folder has folderId property only, no fallback to id
    const folderWithFolderId = {
      ...folder,
      folderId: folder.folderId,
    };
    console.log("Navigating with selected folder:", folderWithFolderId);
    setSelectedFolder(folderWithFolderId);
    setFolderDialogOpen(false);
    navigate("/classifier");
  };

  const createFolderHandler = async (folderName) => {
    try {
      const newFolder = await createFolderMutation.mutateAsync(folderName);

      // Optimistically update the cached folder list
      queryClient.setQueryData(["userFolderMetadata"], (oldFolders = []) => [
        newFolder,
        ...oldFolders,
      ]);

      // Ensure newFolder has folderId property only, no fallback to id
      const newFolderWithFolderId = {
        ...newFolder,
        folderId: newFolder.id, // ROOT CAUSE NG FOLDER ID PAG GAGAWA NG BAGONG FOLDER
      };

      setSelectedFolder(newFolderWithFolderId);
      setFolderDialogOpen(false);
      // Invalidate the userFileStructure query to refetch updated folder list
      queryClient.invalidateQueries(["userFolderMetaData"]);
      navigate("/classifier");
    } catch (error) {
      console.error("Error creating folder:", error);
    }
  };

  const handleFolderDialogCancel = () => {
    setFolderDialogOpen(false);
  };

  const handleFileManagementClick = () => {
    navigate("/file-manager");
  };

  if (isLoading) {
    return <Box>Loading folders...</Box>;
  }

  if (error) {
    return <Box>Error loading folders: {error.message}</Box>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        flexDirection: "column",
        backgroundColor: "#FAF9F6",
      }}
    >
      <FileManagementAppBar handleSignOut={handleSignOut} />

      <Box
        component="main"
        sx={{ flexGrow: 1, pt: 2, backgroundColor: "#FAF9F6", marginTop: 20 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
            width: "100%",
            flexDirection: "row",
            gap: 6,
          }}
        >
          <FileManagementPanel
            onFileManagementClick={handleFileManagementClick}
          />
          <DocumentClassifierPanel
            onOpenFolderDialog={handleOpenFolderDialog}
          />
        </Box>
      </Box>

      {/* Folder Selection Dialog */}
      <FolderSelectionDialog
        open={folderDialogOpen}
        folders={folders}
        selectedFolder={selectedFolder}
        onFolderSelect={handleFolderSelect}
        onCancel={handleFolderDialogCancel}
        onCreateFolder={createFolderHandler}
      />
    </Box>
  );
}

export default UserDashboard;
